import type { GuidedEnvironment } from './guided'

export type EnvParseResult = {
  entries: GuidedEnvironment[]
  /** 1-based line numbers that looked like content but were not `KEY=value`. */
  skippedLines: number[]
}

const KEY_PATTERN = /^[A-Za-z_][A-Za-z0-9_]*$/

/**
 * Parse pasted `.env` text into environment rows.
 *
 * Deliberately a subset of dotenv: one entry per line, an optional `export `,
 * the first `=` splits key from value, and matching single or double quotes
 * are stripped; inside double quotes, `\"` and `\\` are unescaped. An
 * unquoted value keeps everything after `=`, including ` #…`, because
 * guessing at inline comments silently truncates URLs and hashes.
 * Multi-line values are refused — an unclosed quote, or a `\n` escape inside
 * double quotes — rather than half-read.
 * A key repeated in the paste keeps its last value, as dotenv does.
 */
export function parseEnvFile(text: string): EnvParseResult {
  const values = new Map<string, string>()
  const skippedLines: number[] = []
  for (const [index, raw] of text.split(/\r?\n/).entries()) {
    const line = raw.trim()
    if (line === '' || line.startsWith('#')) continue
    const body = line.startsWith('export ') ? line.slice('export '.length).trimStart() : line
    const separator = body.indexOf('=')
    const key = separator < 0 ? '' : body.slice(0, separator).trim()
    const value = separator < 0 ? null : unquote(body.slice(separator + 1).trim())
    if (!KEY_PATTERN.test(key) || value === null) {
      skippedLines.push(index + 1)
      continue
    }
    // A repeat keeps its first position and takes the later value.
    values.set(key, value)
  }
  return { entries: [...values].map(([key, value]) => ({ key, value })), skippedLines }
}

/** Strip one pair of matching quotes; `null` when a quote opens and never closes. */
function unquote(value: string): string | null {
  const quote = value[0]
  if (quote !== '"' && quote !== "'") return value
  if (value.length < 2 || value[value.length - 1] !== quote) return null
  const inner = value.slice(1, -1)
  if (quote === "'") return inner
  // An escaped newline would store a value the single-line input cannot show,
  // and the next edit there would drop it: refuse it, as multi-line values are.
  if (/(^|[^\\])(\\\\)*\\n/.test(inner)) return null
  return inner.replace(/\\(["\\])/g, '$1')
}

const SECRET_TOKENS = new Set(['PASSWORD', 'PASSWD', 'PWD', 'SECRET', 'TOKEN', 'CREDENTIAL', 'CREDENTIALS', 'APIKEY', 'PRIVATE'])
// EXTRA_KEYS: an S3 instance's per-app keys, secrets included (S3_EXTRA_KEYS).
const SECRET_PAIRS = ['API_KEY', 'ACCESS_KEY', 'PRIVATE_KEY', 'EXTRA_KEYS']

/**
 * Whether a key's name suggests it holds a secret. Token-based, so
 * `KEYBOARD_LAYOUT` and `PASSTHROUGH` do not match; advisory only, since the
 * console has no secret store to point the tenant to instead.
 */
export function looksSecret(key: string): boolean {
  const upper = key.trim().toUpperCase()
  if (upper === '') return false
  const tokens = upper.split('_')
  if (tokens.some((token) => SECRET_TOKENS.has(token))) return true
  return SECRET_PAIRS.some((pair) => {
    const [first, second] = pair.split('_')
    return tokens.some((token, index) => token === first && tokens[index + 1] === second)
  })
}

/** Indexes of rows whose trimmed, non-empty key already appeared at a lower index. */
export function duplicateKeyIndexes(env: GuidedEnvironment[]): Set<number> {
  const seen = new Set<string>()
  const duplicates = new Set<number>()
  for (const [index, row] of env.entries()) {
    const key = row.key.trim()
    if (key === '') continue
    if (seen.has(key)) duplicates.add(index)
    else seen.add(key)
  }
  return duplicates
}
